import axios from 'axios';
import type { UserResponseT } from '@mentimeter/http-clients';
import { fetchRegion } from '@mentimeter/region';
import type { VisitorToken } from '@mentimeter/visitor/visitor-token';
import { LocalStorage } from '@mentimeter/storage';
import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type { UtmFirst, UtmLast } from '@mentimeter/utm';
import type {
  AuthResponseT,
  UserClientAuthConfigT,
  UserApiClientT,
  SignupFunctionParamsT,
  SocialAuthProvider,
} from '../../types';
import { ServerAuthError, ClientAuthError, ErrorCodes } from './errors';

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

interface UserCreatePostBase {
  email: string;
  utm_first: UtmFirst | undefined;
  utm_last: UtmLast | undefined;
  registration_page: string;
  visitor_token: VisitorToken | null;
  invite_token?: string | undefined;
}
interface UserCreatePostPassword extends UserCreatePostBase {
  password: string;
  name: string;
}

interface UserCreatePostSocial extends UserCreatePostBase {
  id: string;
  access_token: string;
  network: SocialAuthProvider;
}

interface UserLoginPost {
  email: string;
  password: string;
  visitor_token: VisitorToken | null;
}

const createUserHttpClient = (clientBaseUrl: string) => {
  const axiosClient = axios.create({
    baseURL: clientBaseUrl,
    headers: defaultHeaders,
  });

  return {
    createUser(
      postData: UserCreatePostPassword | UserCreatePostSocial,
      baseURL: string,
    ) {
      return axiosClient.post<UserResponseT>(
        `/users`,
        postData,
        baseURL ? { baseURL } : {},
      );
    },
    loginUser(postData: UserLoginPost, baseURL: string) {
      return axiosClient.post<UserResponseT>(
        '/users/client-auth',
        postData,
        baseURL ? { baseURL } : {},
      );
    },
  };
};

export function createUserApiAuthClient({
  baseUrl: defaultBaseUrl,
}: UserClientAuthConfigT): UserApiClientT {
  const clientBaseUrl = defaultBaseUrl ?? 'no-core-set-build-time';
  const userClient = createUserHttpClient(clientBaseUrl);

  const getRegion = async (email: string) => {
    try {
      const data = await fetchRegion(email, clientBaseUrl);
      return data;
    } catch (e) {
      captureException(
        new MentiError('Region request error', {
          cause: e,
          feature: 'platform',
        }),
      );

      throw e;
    }
  };

  return {
    async signup({
      email,
      password,
      name,
      referral,
      utmFirst,
      utmLast,
      visitorToken,
      regionOverride,
      inviteToken,
    }: SignupFunctionParamsT): Promise<AuthResponseT> {
      if (!LocalStorage.isSupported()) {
        throw new ClientAuthError(ErrorCodes.LOCAL_STORAGE, 'signup');
      }

      let userRegion;
      try {
        userRegion = await getRegion(email);
      } catch (err) {
        throw new ServerAuthError(err, 'signup');
      }

      if (userRegion.exists) {
        throw new ClientAuthError(ErrorCodes.EMAIL_NOT_UNIQUE, 'signup');
      }

      let coreUrl = userRegion.service_urls.core;
      // Override region if regionOverride is provided
      if (regionOverride) {
        const overrideCoreUrl =
          regionOverride === 'us'
            ? process.env.NEXT_PUBLIC_US_CORE_URL
            : process.env.NEXT_PUBLIC_EU_CORE_URL;
        if (overrideCoreUrl) {
          coreUrl = overrideCoreUrl;
        }
      }

      const postData: UserCreatePostPassword = {
        email,
        password,
        name,
        registration_page: referral,
        utm_first: utmFirst,
        utm_last: utmLast,
        visitor_token: visitorToken,
        invite_token: inviteToken,
      };

      try {
        const { data } = await userClient.createUser(postData, coreUrl);
        return {
          mentiUser: data,
          isNewUser: true,
        };
      } catch (err: any) {
        throw new ServerAuthError(err, 'signup');
      }
    },

    async login(
      email: string,
      password: string,
      visitorToken: VisitorToken | null,
    ): Promise<AuthResponseT> {
      if (!LocalStorage.isSupported()) {
        throw new ClientAuthError(ErrorCodes.LOCAL_STORAGE, 'login');
      }
      try {
        const region = await getRegion(email);
        const { data } = await userClient.loginUser(
          {
            email,
            password,
            visitor_token: visitorToken,
          },
          region.service_urls.core,
        );
        return {
          mentiUser: data,
          isNewUser: false,
        };
      } catch (err: any) {
        throw new ServerAuthError(err, 'login');
      }
    },
  };
}
